import { CardDescription, CardHeader } from '@/components/ui/card';
import PaddockAvatar from '@/components/ui/paddock-avatar/paddock-avatar';
import { toast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { commentStore, dealStore } from '@/models/stores';
import type { User } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import type { JSONContent } from '@tiptap/core';
import dayjs from 'dayjs';
import { isEmpty, trim } from 'lodash';
import { ArrowUpCircle, PaperclipIcon } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { getUserMentionIds } from './get-user-mentions-ids';
import PaddockBubbleMenu from './paddock-bubble-menu';
import PdkEditorContent from './pdk-editor-content';
import { usePaddockEditor } from './use-paddock-editor';

type ReplyPayload = {
  id: string;
  content: JSONContent;
  text: string;
  mentions: User['id'][];
};

export const OPTIMISTIC_REPLY_ID_PREFIX = 'optimistic-reply-';

const AddReplyTipTap = ({
  commentId,
  dealId,
}: {
  commentId: string;
  dealId: string;
}) => {
  const { editor } = usePaddockEditor({
    placeholder: 'Leave a reply...',
  });

  const user = useUser();

  const addReply = useMutation({
    onMutate: async ({ reply }) => {
      dealStore.addReplyV3({
        reply: {
          id: reply.id,
          comment_id: commentId,
          created_at: dayjs().toISOString(),
          updated_at: dayjs().toISOString(),
          edited: false,
          text: reply.text,
          mentions: reply.mentions,
          user_id: user.id,
          content: reply.content,
        },
        id: dealId,
      });
    },
    mutationFn: async ({ reply }: { reply: ReplyPayload }) => {
      if (!reply) return;

      const response = await pdkAxios
        .post(`/deals/${dealId}/comments/v3/${commentId}/reply`, {
          id: reply.id,
          content: reply.content,
          text: reply.text,
          mentions: reply.mentions,
        })
        .catch((e) => {
          console.error('Error adding polymorphic reply', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      editor?.commands.setContent({});
    },
    onError: (error, variables) => {
      editor?.commands.setContent(variables?.reply?.content);

      console.error('Error adding polymorphic reply', error, variables);

      toast({
        title: 'Error adding reply',
        description: error.message,
      });
    },
  });

  const {
    // id: userId,
    first_name,
    last_name,
    //  email, role,
    avatar,
  } = useUser();
  const handleSubmitReply = () => {
    const content = editor?.getJSON();

    const text = editor?.getText() || '';

    if (isEmpty(trim(text))) {
      return;
    }

    const mentionIds = getUserMentionIds(editor);

    const payload = {
      reply: {
        id: uuidv4(),
        content: content || {},
        text,
        mentions: mentionIds,
      },
    };

    editor?.commands.setContent({});

    if (!!commentStore.getById(commentId)) {
      addReply.mutate(payload);
    }
  };

  return (
    <CardHeader className="p-0">
      <CardDescription className="flex flex-row gap-2 items-start pt-2">
        <PaddockAvatar
          src={avatar ?? ''}
          alt={`${first_name} ${last_name}`}
          fallback={`${first_name.charAt(0)} ${last_name.charAt(0)}`}
          className="size-4 mt-0.5"
        />
        <div className="w-full">
          <PaddockBubbleMenu editor={editor} />
          <PdkEditorContent
            editor={editor}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.metaKey) {
                e.preventDefault();
                handleSubmitReply();
              }

              if (e.key === HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE) {
                editor?.commands.blur();
              }
            }}
          />
        </div>
        <div className="flex flex-row w-fit justify-end gap-2 h-10 items-center">
          <PaperclipIcon
            className={cn(
              'h-5 w-5 cursor-pointer text-gray-500 hover:fill-gray-100',
              {},
            )}
            onClick={() => {
              toast({
                title: 'Feature not implemented yet',
                description:
                  "Slack us if you want the 'reply attachments' feature",
              });
            }}
          />
          <ArrowUpCircle
            className={cn(
              'h-5 w-5 cursor-pointer text-gray-500 hover:fill-gray-100',
            )}
            onClick={handleSubmitReply}
          />
        </div>
      </CardDescription>
    </CardHeader>
  );
};

export default AddReplyTipTap;
